// The chart language every chart on the web shares (spec 010 FR-033):
// round value scales, marks at round steps along dates, distances, and
// times, and the 7-day average drawn over noisy daily measures. This file
// imports nothing, so its tests run under node without the browser's
// globals.

export interface Scale {
  lo: number;
  hi: number;
  ticks: number[];
  step: number;
}

// DURATION_STEPS are the round steps of a scale in seconds, so a pace or a
// record time is marked at 0:15, 0:30, or whole minutes rather than at 0:25.
export const DURATION_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400];

// niceTicks is a value scale of 3 to 5 round values covering lo to hi: the
// smallest step of 1, 2, 2.5, or 5 times a power of ten (or of steps, when
// given) that needs no more than 5 values, 2.5 only from 25 up so it never
// adds a decimal place. zero starts the scale at 0, as
// bars do; whole keeps every value a whole number, as counts need; max
// lowers the most values to 3 or 4 for a short chart. A single value gets
// a scale around it, and no values a scale of 0 to 1.
export function niceTicks(lo: number, hi: number, opts: { zero?: boolean; whole?: boolean; steps?: number[]; max?: number } = {}): Scale {
  const most = opts.max ?? 5;
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    lo = 0;
    hi = 1;
  }
  if (opts.zero) {
    lo = Math.min(0, lo);
    hi = Math.max(0, hi);
  }
  if (!(hi > lo)) {
    const pad = opts.zero || opts.whole ? 1 : Math.max(Math.abs(lo) * 0.05, 1e-6);
    if (opts.zero && lo === 0) hi = pad;
    else {
      lo -= pad;
      hi += pad;
    }
  }
  const fits = (s: number) => {
    const a = Math.floor(lo / s + 1e-9) * s;
    const b = Math.ceil(hi / s - 1e-9) * s;
    return Math.round((b - a) / s) + 1 <= most;
  };
  let step = 0;
  for (const s of opts.steps ?? []) {
    if (fits(s)) {
      step = s;
      break;
    }
  }
  if (!step) {
    let p = 10 ** (Math.floor(Math.log10((hi - lo) / most)) - 1);
    while (!step) {
      for (const m of [1, 2, 2.5, 5]) {
        const s = m * p;
        if ((opts.whole && s < 1) || (m === 2.5 && s < 10)) continue;
        if (fits(s)) {
          step = s;
          break;
        }
      }
      p *= 10;
    }
  }
  const a = Math.floor(lo / step + 1e-9) * step;
  let b = Math.ceil(hi / step - 1e-9) * step;
  while (Math.round((b - a) / step) + 1 < 3) b += step;
  const ticks: number[] = [];
  for (let v = a; v <= b + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
  return { lo: a, hi: b, ticks, step };
}

// decimals is how many places a scale's values need to read apart at its
// step: 0 for 5, 1 for 0.5, 2 for 0.25.
export function decimals(step: number): number {
  for (let d = 0; d < 4; d++) {
    if (Math.abs(step * 10 ** d - Math.round(step * 10 ** d)) < 1e-6) return d;
  }
  return 4;
}

// linearTicks marks 0 to span at the smallest of steps giving no more than
// max marks, as an activity's kilometers or minutes are.
export function linearTicks(span: number, max: number, steps: number[]): number[] {
  const step = steps.find((s) => Math.floor(span / s + 1e-9) + 1 <= max) ?? steps[steps.length - 1];
  const out: number[] = [];
  for (let v = 0; v <= span + 1e-9; v += step) out.push(v);
  return out;
}

// durationLabel writes a duration mark: "45m", "2h", or "1h 30m".
export function durationLabel(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m`;
  return m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60}h`;
}

// ymd splits a YYYY-MM-DD day, and dayOf writes one back.
function ymd(day: string): [number, number, number] {
  const [y, m, d] = day.split('-').map(Number);
  return [y, m, d];
}

function dayOf(t: number): string {
  const d = new Date(t);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function utc(day: string): number {
  const [y, m, d] = ymd(day);
  return Date.UTC(y, m - 1, d);
}

// daysBetween counts the days from a to b, negative when b comes first.
export function daysBetween(a: string, b: string): number {
  return Math.round((utc(b) - utc(a)) / 864e5);
}

// addDays moves a YYYY-MM-DD day by n days.
export function addDays(day: string, n: number): string {
  return dayOf(utc(day) + n * 864e5);
}

export type DateStep = 'day' | 'week' | 'month' | 'year';

// dateTicks marks the days from to to at the first round step giving no
// more than max marks: every day or 2 days, every week or 2 weeks from the
// viewer's first day of the week (weekStart, 0 Sunday), every 1, 2, 3, or 6
// months on their first day, or every year. unit says how to write them.
export function dateTicks(from: string, to: string, max: number, weekStart: number): { days: string[]; unit: DateStep } {
  const span = daysBetween(from, to);
  const every = (first: string, n: number) => {
    const out: string[] = [];
    for (let d = first; d <= to; d = addDays(d, n)) out.push(d);
    return out;
  };
  for (const n of [1, 2]) {
    if (Math.floor(span / n) + 1 <= max) return { days: every(from, n), unit: 'day' };
  }
  const lead = (weekStart - new Date(utc(from)).getUTCDay() + 7) % 7;
  for (const n of [7, 14]) {
    const days = every(addDays(from, lead), n);
    if (days.length <= max) return { days, unit: 'week' };
  }
  const [fy, fm, fd] = ymd(from);
  for (const k of [1, 2, 3, 6, 12]) {
    const days: string[] = [];
    let y = fy, m = fm + (fd > 1 ? 1 : 0);
    for (;;) {
      if (m > 12) {
        y += Math.floor((m - 1) / 12);
        m = ((m - 1) % 12) + 1;
      }
      const d = `${y}-${String(m).padStart(2, '0')}-01`;
      if (d > to) break;
      if ((m - 1) % k === 0) days.push(d);
      m++;
    }
    if (days.length <= max) return { days, unit: k === 12 ? 'year' : 'month' };
  }
  const days: string[] = [];
  for (let y = fy + 1; `${y}-01-01` <= to; y++) days.push(`${y}-01-01`);
  return { days, unit: 'year' };
}

// average7 is each day's average of the days with a value among the 7
// ending with it, null when none of them has one. values run a day apart.
export function average7(values: (number | null | undefined)[]): (number | null)[] {
  return values.map((_, i) => {
    let sum = 0, n = 0;
    for (let k = Math.max(0, i - 6); k <= i; k++) {
      const v = values[k];
      if (v != null) {
        sum += v;
        n++;
      }
    }
    return n ? sum / n : null;
  });
}

// yPct is where a value sits down a chart, 0 at hi and 100 at lo; a scale
// passed with lo above hi draws upside down, as pace does.
export function yPct(v: number, lo: number, hi: number): number {
  return hi === lo ? 50 : ((hi - v) / (hi - lo)) * 100;
}

// segments splits points at their gaps into the lines a chart draws, each
// [at, value] with at from 0 to 1; joined draws through the gaps instead,
// as a smoothed line is.
export function segments(points: { at: number; v: number | null | undefined }[], joined = false): [number, number][][] {
  const out: [number, number][][] = [];
  let cur: [number, number][] = [];
  for (const p of points) {
    if (p.v == null) {
      if (joined) continue;
      if (cur.length) out.push(cur);
      cur = [];
    } else {
      cur.push([p.at, p.v]);
    }
  }
  if (cur.length) out.push(cur);
  return out;
}

// polyline writes a segment for an SVG drawn in the charts' 0 to 100 box.
export function polyline(seg: [number, number][], lo: number, hi: number): string {
  return seg.map(([at, v]) => `${(at * 100).toFixed(3)},${yPct(v, lo, hi).toFixed(3)}`).join(' ');
}

// runsBy splits a line into stretches of the same key, such as a heart rate
// zone; each stretch starts where the one before ends, so the line stays
// joined where its color changes.
export function runsBy<T, K>(items: T[], key: (t: T) => K): { key: K; items: T[] }[] {
  const out: { key: K; items: T[] }[] = [];
  for (const t of items) {
    const k = key(t);
    const last = out[out.length - 1];
    if (last && last.key === k) last.items.push(t);
    else out.push({ key: k, items: last ? [last.items[last.items.length - 1], t] : [t] });
  }
  return out;
}
